import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { AlertifyService } from '@liebherr/old-sage-ui';
import { asyncScheduler, EMPTY, Observable } from 'rxjs';
import { filter, first, map, observeOn, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { FirmwareUpdateState, RoutersAPI, RouterSorting, Sorting, VpnLinkedState } from '../api/core';
import { AppState } from '../app.models';
import * as fromRouter from '../router';
import { authorizeEffect } from '../shared/operators/authorize-effect';
import { finalizeEffect } from '../shared/operators/finalize-effect';
import * as fromUsers from '../users';
import { formatDeviceDate } from '../utils';
import * as fromDevices from './';
import { DeviceActionTypes, DeviceMenuAction, getDevicePermissions } from './devices.models';
import { FilterControlKeys } from '../shared/shared.models';
import { ParamsKey } from '../router';

@Injectable()
export class DevicesEffects {
    routerState$ = this.store.select(fromRouter.selectRouterState);
    loggedInuser$: Observable<fromUsers.LoggedInUser | undefined> = this.store.select(fromUsers.selectLoggedInUser);

    onNavigate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(routerNavigatedAction),
            withLatestFrom(this.routerState$),
            filter(([action, routerState]) => routerState?.url.startsWith('/devices')),
            observeOn(asyncScheduler),
            map(([action, routerState]) => {
                return fromDevices.loadDevices({ params: routerState?.filterParams });
            })
        );
    });
    load$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromDevices.loadDevices),
            authorizeEffect(this.loggedInuser$, (a, user) => getDevicePermissions('loadAll')),
            tap(() => this.store.dispatch(fromDevices.loading())),
            switchMap((action) => {
                return (
                    // of({ entries: this.getMockRouter(), totalEntries: this.getMockRouter().length })
                    this.api
                        .getAllRouters({
                            craneId: action?.params.craneNumber, // Equipment
                            vpnState: action?.params.connectionState as VpnLinkedState,
                            fwDownloadAllowedBy: action?.params.fwReleasedBy,
                            fwDownloadVersion: action?.params.targetFw,
                            fwUpdateState: action?.params.fwState as FirmwareUpdateState,
                            fwVersion: action?.params[ParamsKey.INSTALLED_FW],
                            page: action?.params.page,
                            pageSize: action?.params.rowLimit,
                            serialNumber: action?.params.serialNumber,
                            routerId: action?.params.routerId,
                            routerTypeId: action?.params.routerType, // Device Type mapped as Number
                            sort: action?.params.sortAsc === true ? Sorting.Asc : Sorting.Desc,
                            sortBy: action?.params.sortColumn as RouterSorting,
                            hasSimcard: action?.params.hasSimcard,
                            isOnline: action?.params.onlineState ? (action?.params.onlineState === 'ONLINE' ? true : false) : undefined,
                            vpnIp: action?.params[ParamsKey.VPN_IP],
                        })
                        .pipe(
                            map((res) => {
                                return fromDevices.loadedDevices({
                                    data: res,
                                });
                            }),
                            finalizeEffect(
                                () => this.store.dispatch(fromDevices.loadingDone()),
                                () =>
                                    this.translateService
                                        .selectTranslate('ALERT.LOADING_FAILED')
                                        .pipe(first())
                                        .subscribe((message) => this.alertifyService.log(message, AlertifyService.ERROR, 'bottom left'))
                            )
                        )
                );
            })
        );
    });
    loadSingleDevice$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromDevices.loadSingleDevice),
            authorizeEffect(this.loggedInuser$, (a, user) => getDevicePermissions('loadSingleDevice')),
            tap(() => this.store.dispatch(fromDevices.loading())),
            switchMap((action) => {
                return this.api
                    .getRouterById({
                        id: action.routerId,
                    })
                    .pipe(
                        map((res) => {
                            return fromDevices.loadedSingleDevice({
                                device: res,
                            });
                        }),
                        finalizeEffect(
                            () => this.store.dispatch(fromDevices.loadingDone()),
                            () =>
                                this.translateService
                                    .selectTranslate('ALERT.LOADING_FAILED')
                                    .pipe(first())
                                    .subscribe((message) => this.alertifyService.log(message, AlertifyService.ERROR, 'bottom left')),
                            DeviceActionTypes.LOADED_SINGLE_DEVICE
                        )
                    );
            })
        );
    });

    addDevice$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromDevices.addDevice),
            authorizeEffect(this.loggedInuser$, (a, user) => getDevicePermissions('addDevice')),
            tap(() => this.store.dispatch(fromDevices.loading())),
            switchMap((action) => {
                return this.api
                    .postNewRouter({
                        body: formatDeviceDate(action.device),
                    })
                    .pipe(
                        map((res: any) => {
                            // type RouterCreationResponse is correct here | api generation is wrong
                            this.alertifyService.log(
                                this.translateService.translate('ALERT.DEVICES.CREATE_SUCCESS'),
                                AlertifyService.SUCCESS,
                                'bottom left'
                            );
                            return fromDevices.addedDevice({
                                device: res.created && res.serialNumber === action.device.serialNumber ? action.device : undefined,
                            });
                        }),
                        finalizeEffect(
                            () => this.store.dispatch(fromDevices.loadingDone()),
                            () =>
                                this.alertifyService.log(
                                    this.translateService.translate('ALERT.CREATE_FAILED'),
                                    AlertifyService.ERROR,
                                    'bottom left'
                                ),
                            DeviceActionTypes.ADDED_DEVICE
                        )
                    );
            })
        );
    });
    addDevicesBulk$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromDevices.addDevicesBulk),
            authorizeEffect(this.loggedInuser$, (a, user) => getDevicePermissions('addDevice')),
            tap(() => this.store.dispatch(fromDevices.loading())),
            switchMap((action) => {
                return this.api
                    .postNewList({
                        body: {
                            newRouters: action.devices.map((d) => formatDeviceDate(d)),
                        },
                    })
                    .pipe(
                        map((res) => {
                            const notCreated = res?.filter((r) => r.created === false).length;
                            const alert =
                                notCreated === 0
                                    ? this.translateService.translate('ALERT.CREATE_DEVICES_SUCCESS')
                                    : this.translateService.translate('ALERT.CREATE_DEVICES_SUCCESS_DUBLETTES', { notCreated });

                            this.alertifyService.log(alert, AlertifyService.SUCCESS, 'bottom left');
                            return fromDevices.addedDevicesBulk({
                                devices: action.devices?.filter((d) => res.find((r) => r.serialNumber === d.serialNumber)?.created),
                            });
                        }),
                        finalizeEffect(
                            () => this.store.dispatch(fromDevices.loadingDone()),
                            () =>
                                this.alertifyService.log(
                                    this.translateService.translate('ALERT.DEVICES.CREATE_BULK_FAILED'),
                                    AlertifyService.ERROR,
                                    'bottom left'
                                ),
                            DeviceActionTypes.ADDED_DEVICES_BULK
                        )
                    );
            })
        );
    });

    delete$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromDevices.deleteDevice),
            authorizeEffect(this.loggedInuser$, (a, user) => getDevicePermissions(DeviceMenuAction.DELETE_DEVICE)),
            tap(() => this.store.dispatch(fromDevices.loading())),
            switchMap((action) => {
                return this.api
                    .deleteRouterById({
                        id: action.id,
                    })
                    .pipe(
                        map(() => {
                            this.alertifyService.log(
                                this.translateService.translate('ALERT.DEVICES.DELETE_SUCCESS'),
                                AlertifyService.SUCCESS,
                                'bottom left'
                            );
                            return fromDevices.deletedDevice({ id: action.id });
                        }),
                        finalizeEffect(
                            () => this.store.dispatch(fromDevices.loadingDone()),
                            () =>
                                this.alertifyService.log(
                                    this.translateService.translate('ALERT.DEVICES.DELETE_FAILED'),
                                    AlertifyService.ERROR,
                                    'bottom left'
                                ),
                            DeviceActionTypes.DELETED_DEVICE
                        )
                    );
            })
        );
    });

    getMasterPassword$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromDevices.loadMasterPassword),
            authorizeEffect(this.loggedInuser$, (a, user) => getDevicePermissions(DeviceMenuAction.MASTER_PASSWORT)),
            tap(() => this.store.dispatch(fromDevices.loading())),
            switchMap((action) => {
                return this.api
                    .getMasterPasswordById({
                        id: action.id,
                    })
                    .pipe(
                        map((res) => {
                            return fromDevices.loadedMasterPassword({ password: res });
                        }),
                        finalizeEffect(
                            () => this.store.dispatch(fromDevices.loadingDone()),
                            undefined,
                            DeviceActionTypes.LOADED_MASTER_PASSWORD
                        )
                    );
            })
        );
    });

    disconnectDevice$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromDevices.disconnectDeviceFromVPN),
            authorizeEffect(this.loggedInuser$, (a, user) =>
                getDevicePermissions(DeviceMenuAction.VPN_DISCONNECT, user, {
                    vpnInfo: { currentUserId: a.connectedUser },
                })
            ),
            tap(() => this.store.dispatch(fromDevices.loading())),
            withLatestFrom(this.loggedInuser$),
            switchMap(([action, loggedInUser]) => {
                let apiCall: Observable<any>;

                if (loggedInUser?.username === action.connectedUser) {
                    // user disconnects himself
                    apiCall = this.api.deleteVpnConnection({
                        id: action.id,
                    });
                } else {
                    if (action.connectedUser) {
                        // admin disconnects other user
                        apiCall = this.api.routersIdVpnConnectionUsernameDelete({
                            id: action.id,
                            username: action.connectedUser,
                        });
                    } else {
                        // admin disconnects vpn with lost connection (LIEB-534)
                        apiCall = this.api.unlinkRouter({
                            id: action.id,
                        });
                    }
                }

                return apiCall.pipe(
                    map(() => {
                        this.alertifyService.log(
                            this.translateService.translate('ALERT.DEVICES.DISCONNECT_SUCCESS'),
                            AlertifyService.SUCCESS,
                            'bottom left'
                        );
                        return fromDevices.disconnectedDeviceFromVPN({ id: action.id });
                    }),
                    finalizeEffect(
                        () => this.store.dispatch(fromDevices.loadingDone()),
                        () =>
                            this.alertifyService.log(
                                this.translateService.translate('ALERT.DEVICES.DISCONNECT_FAILED'),
                                AlertifyService.ERROR,
                                'bottom left'
                            ),
                        DeviceActionTypes.DISCONNECTED_DEVICE
                    )
                );
            })
        );
    });
    activateDevice$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromDevices.activateDevice),
            authorizeEffect(this.loggedInuser$, (a, user) => getDevicePermissions(DeviceMenuAction.ACTIVATE_DEVICE)),
            tap(() => this.store.dispatch(fromDevices.loading())),
            switchMap((action) => {
                return this.api
                    .postActivation({
                        id: action.id,
                    })
                    .pipe(
                        map(() => {
                            this.alertifyService.log(
                                this.translateService.translate('ALERT.DEVICES.ACTIVATE_SUCCESS'),
                                AlertifyService.SUCCESS,
                                'bottom left'
                            );
                            return fromDevices.activatedDevice({ id: action.id });
                        }),
                        finalizeEffect(
                            () => this.store.dispatch(fromDevices.loadingDone()),
                            () =>
                                this.alertifyService.log(
                                    this.translateService.translate('ALERT.DEVICES.ACTIVATE_FAILED'),
                                    AlertifyService.ERROR,
                                    'bottom left'
                                ),
                            DeviceActionTypes.ACTIVATED_DEVICE
                        )
                    );
            })
        );
    });
    allowCertificateRenewal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromDevices.allowCertificateRenewal),
            authorizeEffect(this.loggedInuser$, (a, user) => getDevicePermissions(DeviceMenuAction.CERTIFICATE_RENEWAL)),
            tap(() => this.store.dispatch(fromDevices.loading())),
            switchMap((action) => {
                return this.api
                    .postCertificateRenewal({
                        id: action.id,
                    })
                    .pipe(
                        map(() => {
                            this.alertifyService.log(
                                this.translateService.translate('ALERT.DEVICES.ALLOW_CERIFICATE_RENEWAL_SUCCESS'),
                                AlertifyService.SUCCESS,
                                'bottom left'
                            );
                            return fromDevices.allowedCertificateRenewal({ id: action.id });
                        }),
                        finalizeEffect(
                            () => this.store.dispatch(fromDevices.loadingDone()),
                            () =>
                                this.alertifyService.log(
                                    this.translateService.translate('ALERT.DEVICES.ALLOW_CERIFICATE_RENEWAL_FAILED'),
                                    AlertifyService.ERROR,
                                    'bottom left'
                                ),
                            DeviceActionTypes.ALLOWED_CERIFICATE_RENEWAL
                        )
                    );
            })
        );
    });
    allowFwActivation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromDevices.allowFwActivation),
            authorizeEffect(this.loggedInuser$, (a, user) => getDevicePermissions(DeviceMenuAction.FW_ACTIVATION)),
            tap(() => this.store.dispatch(fromDevices.loading())),
            switchMap((action) => {
                return this.api
                    .postFirmwareActivation({
                        routerId: action.id,
                    })
                    .pipe(
                        map(() => {
                            this.alertifyService.log(
                                this.translateService.translate('ALERT.DEVICES.ALLOW_FW_ACTIVATION_SUCCESS'),
                                AlertifyService.SUCCESS,
                                'bottom left'
                            );
                            return fromDevices.allowedFwActivation({ id: action.id });
                        }),
                        finalizeEffect(
                            () => this.store.dispatch(fromDevices.loadingDone()),
                            () =>
                                this.alertifyService.log(
                                    this.translateService.translate('ALERT.DEVICES.ALLOW_FW_ACTIVATION_FAILED'),
                                    AlertifyService.ERROR,
                                    'bottom left'
                                ),
                            DeviceActionTypes.ALLOWED_FW_ACTIVATION
                        )
                    );
            })
        );
    });
    allowFwDownload$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromDevices.allowFwDownload),
            authorizeEffect(this.loggedInuser$, (a, user) => getDevicePermissions(DeviceMenuAction.FW_DONWLOAD)),
            tap(() => this.store.dispatch(fromDevices.loading())),
            switchMap((action) => {
                return this.api
                    .postFirmwareUpdate({
                        routerId: action.id,
                    })
                    .pipe(
                        map(() => {
                            this.alertifyService.log(
                                this.translateService.translate('ALERT.DEVICES.ALLOW_FW_DOWNLOAD_SUCCESS'),
                                AlertifyService.SUCCESS,
                                'bottom left'
                            );
                            return fromDevices.allowedFwDownload({ id: action.id });
                        }),
                        finalizeEffect(
                            () => this.store.dispatch(fromDevices.loadingDone()),
                            () =>
                                this.alertifyService.log(
                                    this.translateService.translate('ALERT.DEVICES.ALLOW_FW_DOWNLOAD_FAILED'),
                                    AlertifyService.ERROR,
                                    'bottom left'
                                ),
                            DeviceActionTypes.ALLOWED_FW_DOWNLOAD
                        )
                    );
            })
        );
    });
    forceFwUpdate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromDevices.forceFwUpdate),
            authorizeEffect(this.loggedInuser$, (a, user) => getDevicePermissions(DeviceMenuAction.FW_FORCE)),
            tap(() => this.store.dispatch(fromDevices.loading())),
            switchMap((action) => {
                return this.api
                    .postFirmwareForceUpdate({
                        firmwareId: action.firmwareId,
                        routerId: action.routerId,
                    })
                    .pipe(
                        map(() => {
                            this.alertifyService.log(
                                this.translateService.translate('ALERT.DEVICES.FORCE_FW_UPDATE_SUCCESS'),
                                AlertifyService.SUCCESS,
                                'bottom left'
                            );
                            return fromDevices.forcedFwUpdate({
                                firmwareId: action.firmwareId,
                                routerId: action.routerId,
                            });
                        }),
                        finalizeEffect(
                            () => this.store.dispatch(fromDevices.loadingDone()),
                            () =>
                                this.alertifyService.log(
                                    this.translateService.translate('ALERT.DEVICES.FORCE_FW_UPDATE_FAILED'),
                                    AlertifyService.ERROR,
                                    'bottom left'
                                ),
                            DeviceActionTypes.FORCED_FW_UPDATE
                        )
                    );
            })
        );
    });
    editDevice$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromDevices.editDevice),
            authorizeEffect(this.loggedInuser$, (a, user) => getDevicePermissions(DeviceMenuAction.EDIT)),
            tap(() => this.store.dispatch(fromDevices.loading())),
            switchMap((action) => {
                if (action.device.id) {
                    return this.api.updateRouter({ id: action.device.id, body: action.device }).pipe(
                        map(() => {
                            this.alertifyService.log(
                                this.translateService.translate('ALERT.DEVICES.EDIT_DEVICE_SUCCESS'),
                                AlertifyService.SUCCESS,
                                'bottom'
                            );
                            return fromDevices.updateDevice({
                                event: { ...action.device, routerId: action.device?.id },
                            });
                        }),
                        finalizeEffect(
                            () => this.store.dispatch(fromDevices.loadingDone()),
                            () =>
                                this.alertifyService.log(
                                    this.translateService.translate('ALERT.DEVICES.EDIT_DEVICE_FAILED'),
                                    AlertifyService.ERROR,
                                    'bottom'
                                ),
                            DeviceActionTypes.FORCED_FW_UPDATE
                        )
                    );
                } else {
                    return EMPTY;
                }
            })
        );
    });

    constructor(
        private actions$: Actions,
        private store: Store<AppState>,
        private alertifyService: AlertifyService,
        private translateService: TranslocoService,
        private api: RoutersAPI
    ) {}
}
